import './index.css';
import { useState } from 'react';
import NimGamePage from '../nimGamePage';
import BattleshipGamePage from '../battleShipPage';
import useGamePage from '../../../../hooks/useGamePage';
import { BattleShipGameState, GameInstance, NimGameState } from '../../../../types/types';
import GameChatPage from './chat';

/**
 * Component to display the game page for a specific game type, including controls and game state.
 * @returns A React component rendering:
 * - A header with the game title and current game status.
 * - A "Leave Game" button to exit the current game.
 * - The game component specific to the game type (e.g., `NimGamePage` for "Nim").
 * - An error message if an error occurs during the game.
 */
const GamePage = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { gameInstance, error, handleLeaveGame } = useGamePage();

  /**
   * Renders the appropriate game component based on the game type.
   * @param gameType The type of the game to render (e.g., "Nim").
   * @returns A React component corresponding to the specified game type, or a
   * fallback message for unknown types.
   */
  const renderGameComponent = (gameType: string) => {
    if (!gameInstance) return null;

    switch (gameType) {
      case 'Nim':
        return <NimGamePage gameInstance={gameInstance as GameInstance<NimGameState>} />;
      case 'BattleShip':
        return (
          <BattleshipGamePage gameInstance={gameInstance as GameInstance<BattleShipGameState>} />
        );
      default:
        return <div>Unknown game type</div>;
    }
  };

  return (
    <div className='game-page'>
      <div className='game-header'>
        <h1 className='game-title'>{gameInstance ? gameInstance.gameType : 'Game not found'}</h1>
        <p className='room-code'>
          {!gameInstance?.isPublic && (
            <span>
              Room Code: <strong>{gameInstance?.gameID}</strong>
            </span>
          )}
        </p>
        <button className='btn-leave-game' onClick={handleLeaveGame}>
          Leave Game
        </button>
      </div>

      {gameInstance && renderGameComponent(gameInstance.gameType)}

      {error && <div className='game-error'>{error}</div>}

      <div className={`chat-room-container ${isExpanded ? 'expanded' : 'collapsed'}`}>
        <div className='chat-header' onClick={() => setIsExpanded(!isExpanded)}>
          <h2 className='chat-title'>Chat Room</h2>
          <span className='toggle-icon'>{isExpanded ? '−' : '+'}</span>
        </div>

        {isExpanded && <GameChatPage hideHeader={true} />}
      </div>
    </div>
  );
};

export default GamePage;
